import React, { useState, useEffect } from "react";
import { Upload, message, Image, Button } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import "./imageUpload.css";

const ImageUpload = ({ value, onChange, maxSize = 5, accept = "image/*" }) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(value || "");

  useEffect(() => {
    setImageUrl(value || "");
  }, [value]);

  const handleChange = (url) => {
    setImageUrl(url || "");
    onChange?.(url);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }
    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(`Image must be smaller than ${maxSize}MB!`);
      return false;
    }
    return true;
  };

  const handleUpload = async (file) => {
    if (!beforeUpload(file)) {
      return false;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      let getRes = (res) => {
        if (res.status === 200) {
          // Handle direct url field: { "url": "..." }
          // Also handle nested structures for compatibility
          const url = res?.data?.url ;
          if (url) {
            handleChange(url);
            message.success("Image uploaded successfully!");
          } else {
            console.log("Upload response:", res);
            message.error("Upload successful but no URL returned");
          }
        }
        setUploading(false);
      };

      callApi(
        "POST",
        routes.uploadImage,
        formData,
        setUploading,
        getRes,
        (error) => {
          console.log("Upload error:", error);
          const errorMsg = error?.response?.data?.message || "Failed to upload image";
          message.error(errorMsg);
          setUploading(false);
        },
        0,
        true // multipart
      );
    } catch (error) {
      console.log("Upload error:", error);
      message.error("Failed to upload image");
      setUploading(false);
    }

    return false; // Prevent default upload
  };

  const handleRemove = () => {
    handleChange("");
  };

  const uploadProps = {
    name: "file",
    listType: "picture",
    showUploadList: false,
    beforeUpload: handleUpload,
    accept: accept,
    maxCount: 1,
  };

  return (
    <div className="image-upload-container">
      {imageUrl ? (
        <div className="image-preview-container">
          <Image
            src={imageUrl}
            alt="Preview"
            width={200}
            height={200}
            style={{ objectFit: "cover", borderRadius: 8 }}
            preview={{
              mask: "Preview",
            }}
          />
          <div className="image-actions">
            <Upload {...uploadProps} disabled={uploading}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploading}
                size="small"
              >
                Replace
              </Button>
            </Upload>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              size="small"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <Upload {...uploadProps} disabled={uploading}>
          <div className="upload-placeholder">
            <UploadOutlined style={{ fontSize: 32, color: "#999" }} />
            <div style={{ marginTop: 8, color: "#999" }}>
              {uploading ? "Uploading..." : "Click to upload image"}
            </div>
            <div style={{ fontSize: 12, color: "#ccc", marginTop: 4 }}>
              Max size: {maxSize}MB
            </div>
          </div>
        </Upload>
      )}
    </div>
  );
};

export default ImageUpload;
